import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatApiError, roleHome } from "../lib/format";
import { Lock, Mail, Loader2, Building2, Eye, EyeOff } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_b1b732c2-f76b-4b34-905f-135af01bf27f/artifacts/60bo7zef_Copilot_20260617_182205.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/public/settings").then(r => setGoogleClientId(r.data?.google_client_id || "")).catch(()=>{});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      const u = data.user;
      if (data.requires_password_change && data.reset_token) {
        toast.info("This is a temporary password — please set a new password to continue.");
        nav(`/reset-password?token=${data.reset_token}&first_login=1`, { replace: true });
        return;
      }
      toast.success(`Welcome back, ${u.full_name}`);
      nav(location.state?.from?.pathname || roleHome(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const { data } = await api.post("/auth/google", { credential: credentialResponse.credential });
      if (data.access_token) localStorage.setItem("modav_token", data.access_token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.full_name}`);
      nav(roleHome(data.user.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#F7F9F8]">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-[#0A4A2E] to-[#0E5C3A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 sidebar-texture opacity-40"></div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-2xl p-2"><img src={LOGO} alt="Modav" className="w-14 h-14 object-contain"/></div>
          <div>
            <div className="font-serif text-2xl font-bold">Modav Homes</div>
            <div className="text-[#C9A227] italic text-sm">Comfort & Convenience</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-serif text-4xl xl:text-5xl font-bold leading-tight">A modern home for<br/>your tenancy operations.</h1>
          <p className="mt-4 text-white/80 max-w-md">Manage units, leases, payments and tenants of Modav Homes — Ilese-Ijebu, Ogun State — in one elegant, secure dashboard.</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-3xl font-serif font-bold text-[#C9A227]">18</div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Total Units</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-3xl font-serif font-bold text-[#C9A227]">5</div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Roles</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-3xl font-serif font-bold text-[#C9A227]">₦</div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Naira</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/60 flex items-center gap-2"><Building2 size={12}/> Along Ondo/Benin Road, Ilese-Ijebu, Ogun State, Nigeria</div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="bg-[#0E5C3A] rounded-xl p-1.5"><img src={LOGO} alt="Modav" className="w-10 h-10 object-contain"/></div>
            <div>
              <div className="font-serif text-xl font-bold text-[#0E5C3A]">Modav Homes</div>
              <div className="text-[#C9A227] italic text-xs">Comfort & Convenience</div>
            </div>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24]">Welcome back</h2>
          <p className="text-[#5E7166] mt-1 text-sm">Sign in to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Email</span>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)}
                       className="input-modav pl-11" placeholder="you@modavhomes.ng" data-testid="login-email"/>
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Password</span>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
                <input type={showPassword ? "text" : "password"} required value={password} onChange={e=>setPassword(e.target.value)}
                       className="input-modav pl-11 pr-11" placeholder="••••••••" data-testid="login-password"/>
                <button type="button" onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        title={showPassword ? "Hide password" : "Show password"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E7166] hover:text-[#0E5C3A] transition-colors p-1 rounded"
                        data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </label>
            <button type="submit" disabled={loading} className="btn-gold w-full justify-center" data-testid="login-submit-button">
              {loading ? <><Loader2 className="animate-spin" size={16}/> Signing in…</> : "Sign in"}
            </button>

            {googleClientId && (
              <div className="pt-2">
                <div className="flex items-center gap-3 mb-3"><div className="flex-1 h-px bg-[#E5EBE8]"></div><span className="text-xs text-[#5E7166] uppercase tracking-wider">or</span><div className="flex-1 h-px bg-[#E5EBE8]"></div></div>
                <div className="flex justify-center" data-testid="google-signin">
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <GoogleLogin onSuccess={handleGoogleLogin} onError={() => toast.error("Google sign-in failed")} />
                  </GoogleOAuthProvider>
                </div>
              </div>
            )}

            <div className="text-center text-sm">
              <Link to="/forgot-password" className="text-[#0E5C3A] hover:text-[#C9A227] font-medium" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </form>

          <div className="mt-8 text-center text-xs text-[#5E7166]">
            <p>Don't have an account yet? Your property manager will invite you by email.</p>
            <p className="mt-2"><a href="/" className="text-[#0E5C3A] font-semibold hover:text-[#C9A227]">← View available units</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
